import { useState } from "react";
import { AlertTriangle, Package } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import PackagingEditor, { packagingBody, packagingSummary } from "../products/PackagingEditor";
import PackagingReviewBadge from "./PackagingReviewBadge";

function fromLine(ln) {
  return { unit: ln.unit || "", units_per_pack: ln.units_per_pack ?? "", packs_per_unit: ln.packs_per_unit ?? "", items_per_pack: ln.items_per_pack ?? "",
    item_type: ln.item_type || "", each_size: ln.each_size || "", size_is_capacity: !!ln.size_is_capacity };
}

const describe = (p) => packagingSummary({ ...p, unit: p.unit || "" }) || "no packaging";

// One cell on the invoice review table: summary of the line's packaging, conflict badge, and a popover editor for the hierarchy.
export default function LinePackaging({ line, index, onChange, readOnly }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const summary = packagingSummary(line);
  const conflict = line.packaging_conflict;
  const apply = (fields, extra = {}) => onChange({ ...line, ...fields, ...extra });
  const resolve = (choice) => {
    const src = choice === "saved" ? conflict.saved : conflict.invoice;
    const { unit: _u, pack_size: _p, ...struct } = src; // eslint-disable-line no-unused-vars
    // "Keep saved" is remembered on the product so the same wording/pack difference does not come back on the next invoice
    apply(struct, { packaging_conflict: null, packaging_dismiss: choice === "saved" ? conflict.invoice : null });
  };

  if (readOnly) {
    return <span className="block px-2 py-1 text-xs text-slate-600" data-testid={`line-${index}-packaging`}>{summary || "—"} <PackagingReviewBadge line={line} index={index} /></span>;
  }
  return (
    <div className="min-w-[240px]" data-testid={`line-${index}-packaging`}>
      <PackagingReviewBadge line={line} index={index} />
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setDraft(fromLine(line)); }}>
        <PopoverTrigger asChild>
          <button type="button" className={`w-full text-left rounded border px-2 py-1.5 text-xs leading-snug ${summary ? "border-slate-200 bg-white text-slate-700 hover:border-emerald-400" : "border-rose-200 bg-rose-50 text-rose-700"}`} data-testid={`line-${index}-packaging-button`}>
            <Package className="inline h-3 w-3 mr-1 -mt-0.5 text-slate-400" />{summary ? summary.replace(/^1 \S+ = /, "") : "Set packaging"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(92vw,560px)] p-3" data-testid={`line-${index}-packaging-popover`}>
          <p className="text-xs font-semibold text-slate-700 mb-2 truncate">{line.description || "Line"} — packaging</p>
          {draft && <PackagingEditor value={draft} onChange={setDraft} testId={`line-${index}-packaging`} compact />}
          <div className="mt-2 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`line-${index}-packaging-cancel`}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { apply(packagingBody(draft), { packaging_conflict: null }); setOpen(false); }} data-testid={`line-${index}-packaging-apply`}>Apply to line</Button>
          </div>
        </PopoverContent>
      </Popover>
      {conflict && (
        <div className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900" data-testid={`line-${index}-packaging-conflict`}>
          <p className="font-semibold flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Differs from saved product</p>
          <p>Invoice: <span className="font-medium">{describe({ ...conflict.invoice, unit: conflict.invoice.unit || line.unit })}</span></p>
          <p>Saved: <span className="font-medium">{describe({ ...conflict.saved, unit: conflict.saved.unit || line.unit })}</span></p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <button type="button" onClick={() => resolve("invoice")} className="rounded bg-amber-600 text-white px-2 py-0.5 font-semibold hover:bg-amber-700" data-testid={`line-${index}-conflict-use-invoice`}>Use invoice (pack changed)</button>
            <button type="button" onClick={() => resolve("saved")} className="rounded border border-amber-400 bg-white px-2 py-0.5 font-semibold hover:bg-amber-100" data-testid={`line-${index}-conflict-keep-saved`}>Keep saved</button>
          </div>
        </div>
      )}
    </div>
  );
}
